/* global axios */
import ApiClient from '../ApiClient';

class AgentsAPI extends ApiClient {
  constructor() {
    super('agno/agents', { accountScoped: true });
  }

  get({ page = 1, searchKey } = {}) {
    return axios.get(this.url, {
      params: {
        page,
        search_key: searchKey,
      },
    });
  }

  show(id) {
    return axios.get(`${this.url}/${id}`);
  }

  create(data) {
    return axios.post(this.url, { agent: data });
  }

  update({ id, ...data }) {
    return axios.put(`${this.url}/${id}`, { agent: data });
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }

  providers() {
    return axios.get(`${this.url}/providers`);
  }

  sync(id) {
    return axios.post(`${this.url}/${id}/sync`);
  }

  resetHomologationMemory(id) {
    return axios.post(`${this.url}/${id}/reset_homologation_memory`);
  }

  // assistantId: o escolhido no nó, mesmo antes de salvar o fluxo.
  flexcallsSync(id, assistantId = null) {
    return axios.post(`${this.url}/${id}/flexcalls_sync`, {
      assistant_id: assistantId || undefined,
    });
  }

  // Roteiro (system_prompt + saudação) no formato que vai para o FlexCalls.
  flexcallsPrompt(id) {
    return axios.get(`${this.url}/${id}/flexcalls_prompt`);
  }

  // Homologação: liga de verdade para um telefone com o assistente do agente.
  flexcallsTestCall(id, phone, assistantId = null) {
    return axios.post(`${this.url}/${id}/flexcalls_test_call`, {
      phone,
      assistant_id: assistantId || undefined,
    });
  }

  releaseStatus(id, version) {
    return axios.get(`${this.url}/${id}/release_status`, {
      params: { version },
    });
  }

  quality(id, days = 7) {
    return axios.get(`${this.url}/${id}/quality`, { params: { days } });
  }

  goldenTests(id) {
    return axios.get(`${this.url}/${id}/golden_tests`);
  }

  createGoldenTest(id, data) {
    return axios.post(`${this.url}/${id}/golden_tests`, { golden_test: data });
  }

  deleteGoldenTest(id, testId) {
    return axios.delete(`${this.url}/${id}/golden_tests/${testId}`);
  }

  runGoldenTests(id) {
    return axios.post(`${this.url}/${id}/golden_tests/run`);
  }

  promptSuggestions(id) {
    return axios.get(`${this.url}/${id}/prompt_suggestions`);
  }

  approvePromptSuggestion(id, suggestionId) {
    return axios.post(
      `${this.url}/${id}/prompt_suggestions/${suggestionId}/approve`
    );
  }

  rejectPromptSuggestion(id, suggestionId) {
    return axios.post(
      `${this.url}/${id}/prompt_suggestions/${suggestionId}/reject`
    );
  }

  runPromptDoctor(id) {
    return axios.post(`${this.url}/${id}/prompt_suggestions/run`);
  }

  syncAll() {
    return axios.post(`${this.url}/sync_all`);
  }

  getTiers() {
    return axios.get(`${this.url}/tiers`);
  }

  // Templates
  getTemplates() {
    return axios.get(`${this.url}/templates`);
  }

  getTemplate(templateId) {
    return axios.get(`${this.url}/templates/${templateId}`);
  }

  createFromTemplate(data) {
    return axios.post(`${this.url}/create_from_template`, data);
  }

  // Tools
  getAvailableTools(sector = null) {
    const params = sector ? { sector } : {};
    return axios.get(`${this.url}/available_tools`, { params });
  }

  // Testa um nó HTTP Request do canvas (faz a requisição e devolve o status).
  testHttpTool(config) {
    return axios.post(`${this.url}/test_http_tool`, config);
  }

  // Inboxes connected to agent
  getInboxes(agentId) {
    return axios.get(`${this.url}/${agentId}/inboxes`);
  }

  getAvailableInboxes(agentId) {
    return axios.get(`${this.url}/${agentId}/inboxes/available`);
  }

  connectInbox(agentId, inboxId, config = {}) {
    return axios.post(`${this.url}/${agentId}/inboxes`, {
      inbox_id: inboxId,
      ...config,
    });
  }

  disconnectInbox(agentId, inboxConnectionId) {
    return axios.delete(`${this.url}/${agentId}/inboxes/${inboxConnectionId}`);
  }

  updateInboxConnection(agentId, inboxConnectionId, data) {
    return axios.put(
      `${this.url}/${agentId}/inboxes/${inboxConnectionId}`,
      data
    );
  }

  // Rules for agent
  getRules(agentId, inboxId = null) {
    const params = inboxId ? { inbox_id: inboxId } : {};
    return axios.get(`${this.url}/${agentId}/rules`, { params });
  }

  createRule(agentId, data) {
    return axios.post(`${this.url}/${agentId}/rules`, { rule: data });
  }

  updateRule(agentId, ruleId, data) {
    return axios.put(`${this.url}/${agentId}/rules/${ruleId}`, { rule: data });
  }

  deleteRule(agentId, ruleId) {
    return axios.delete(`${this.url}/${agentId}/rules/${ruleId}`);
  }

  reorderRules(agentId, ruleIds) {
    return axios.post(`${this.url}/${agentId}/rules/reorder`, {
      rule_ids: ruleIds,
    });
  }

  // Playground - test agent
  testAgent(agentId, data) {
    return axios.post(`${this.url}/${agentId}/test`, data);
  }

  // TTS Options
  getTTSOptions() {
    return axios.get(`${this.url}/tts_options`);
  }

  // Generate Prompt with AI
  generatePrompt(data) {
    return axios.post(`${this.url}/generate_prompt`, data);
  }
}

export default new AgentsAPI();
